import React, { Fragment, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Col, Form, FormGroup, Nav, Row } from "reactstrap";
import Select from "react-select";
import * as yup from "yup";
import ModalFormSubmit from "../../../../share-components/form/modalFormSubmit";
import CommonDataTable from "../../../../share-components/table/commonDataTable";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
  addCourierServiceCost,
  closeEditDialog,
  deleteCourierServiceCost,
  getAllCourierServiceCosts,
  openEditDialog,
  openNewDialog,
  selectCourierServiceCost,
  updateCourierServiceCost,
} from "../../store/courierServiceCostSlice";
import { COURIER_COST_PERMISSION, NEW_ORDERS_ONLY_NOTE, RateInfoNote, filterSelectStyles, rateField } from "./courierCostShared";
import CourierServiceCostsForm from "./courierServiceCostsForm";

const defaultValues = {
  id: "",
  delivery_partner_id: "",
  service_code: "",
  outbound_rate: "",
  inbound_rate: "",
  fuel_percentage: 0,
};

const schema = yup.object().shape({
  delivery_partner_id: yup.string().required("You must select a courier"),
  service_code: yup.string().trim().max(50, "Service code must be at most 50 characters").required("You must enter a service code"),
  outbound_rate: rateField("Outbound rate"),
  inbound_rate: rateField("Inbound rate"),
  fuel_percentage: rateField("Fuel percentage").max(100, "Fuel percentage cannot exceed 100"),
});

function ServiceCostsTab() {
  const dispatch = useDispatch();
  const [serviceCode, setServiceCode] = useState("");
  const [deliveryPartnerId, setDeliveryPartnerId] = useState("");
  const currentUser = useSelector(({ authReducer }) => authReducer.data.user);
  const courierServiceCosts = useSelector(selectCourierServiceCost);
  const deliveryPartners = useSelector(({ partner }) => partner.courierServiceCost.deliveryPartners);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const canManage = currentUser.permission.includes(COURIER_COST_PERMISSION);

  const { handleSubmit, formState, reset, register, setValue, getValues, trigger, watch } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { errors } = formState;

  useEffect(() => {
    getData();
  }, [dispatch, serviceCode, deliveryPartnerId]);

  const getData = () => {
    setTableLoading(true);
    dispatch(getAllCourierServiceCosts({ service_code: serviceCode, delivery_partner_id: deliveryPartnerId })).then(() => {
      setTableLoading(false);
    });
  };

  const tableColumns = [
    {
      name: "Courier",
      selector: (row) => row.delivery_partner_name,
      sortable: true,
      left: true,
    },
    {
      name: "Service Code",
      selector: (row) => row.service_code,
      sortable: true,
      left: true,
    },
    {
      name: "Outbound Rate",
      selector: (row) => row.outbound_rate,
      sortable: true,
      right: true,
    },
    {
      name: "Inbound Rate",
      selector: (row) => row.inbound_rate,
      sortable: true,
      right: true,
    },
    {
      name: "Fuel %",
      selector: (row) => row.fuel_percentage,
      sortable: true,
      right: true,
    },
  ];

  const closeDialog = () => {
    dispatch(closeEditDialog());
  };

  const onEdit = (row) => {
    dispatch(openEditDialog({ ...row, delivery_partner_id: String(row.delivery_partner_id) }));
  };

  const onDelete = (row) => {
    dispatch(deleteCourierServiceCost(row.id)).then(() => getData());
  };

  const onSubmitForm = (dialog, data) => {
    setButtonDisable(true);
    const action = dialog.type === "new" ? addCourierServiceCost : updateCourierServiceCost;
    dispatch(action({ ...data })).then((res) => {
      setButtonDisable(false);
      getData();
      if (!res.error) closeDialog();
    });
  };

  return (
    <Fragment>
      <div className="product-grid">
        <div className="feature-products">
          {canManage ? (
            <Card>
              <Row>
                <Col sm="6">
                  <Nav tabs className="border-tab"></Nav>
                </Col>
                <Col sm="6">
                  <div className="float-sm-right p-3">
                    <a
                      className="btn btn-sm btn-primary"
                      onClick={() => {
                        dispatch(openNewDialog());
                      }}
                    >
                      {" "}
                      <i className="fa fa-plus"></i> Add Courier Service Cost
                    </a>
                  </div>
                </Col>
              </Row>
            </Card>
          ) : (
            ""
          )}
          <Row>
            <Col sm="6">
              <Select
                placeholder={<div>All Couriers</div>}
                className="select2-filter"
                isClearable
                styles={filterSelectStyles}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={deliveryPartners.find((partner) => String(partner.id) === String(deliveryPartnerId)) || null}
                onChange={(e) => setDeliveryPartnerId(e ? e.id : "")}
                options={deliveryPartners}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </Col>
            <Col sm="6">
              <Form>
                <FormGroup className="m-0">
                  <input onChange={(e) => setServiceCode(e.target.value)} type="text" className="form-control border" placeholder="Search By Service Code" />
                  <i className="fa fa-search"></i>
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </div>
      </div>

      <Card>
        <CardBody>
          <RateInfoNote>
            Matched per courier and the booked service code; takes priority over shipping-method and carrier default rates. {NEW_ORDERS_ONLY_NOTE}
          </RateInfoNote>
          {tableLoading ? (
            <div style={{ minHeight: 400 }}>
              <PreLoader />
            </div>
          ) : (
            <div className="table-responsive product-table mt-4">
              <CommonDataTable
                headerColumns={tableColumns}
                gridData={courierServiceCosts}
                pagination
                onEdit={canManage ? onEdit : ""}
                onDelete={canManage ? onDelete : ""}
              />
            </div>
          )}
        </CardBody>
      </Card>

      <ModalFormSubmit
        defaultValues={defaultValues}
        schema={schema}
        onSubmitForm={onSubmitForm}
        reset={reset}
        formState={formState}
        handleSubmit={handleSubmit}
        title="Courier Service Cost"
        selector="partner"
        dialog={({ partner }) => partner.courierServiceCost.addDialog}
        closeDialog={closeDialog}
        setValue={setValue}
        getValues={getValues}
        size="lg"
        buttonDisable={buttonDisable}
      >
        <CourierServiceCostsForm errors={errors} register={register} setValue={setValue} trigger={trigger} watch={watch} deliveryPartners={deliveryPartners} />
      </ModalFormSubmit>
    </Fragment>
  );
}

export default ServiceCostsTab;
